import TextLato from '@photosi/rn-components/text/text-lato/TextLato';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AccessibilityInfo, ScrollView, View } from 'react-native';
import { observer } from 'mobx-react-lite';
import { styles } from './PickerStep.style';
import { SELECTED_MENU_ITEMS } from 'src/store/_models/dynamic-modal-layer/dynamicModalLayer.constants';
import { isWeb } from 'src/_shared/utils/isWeb';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import {
  SelectedMenuItem2SourcesEventTypeMap,
  SourcesEventType,
} from '@photosi/rn-components/modals/dynamic-modals/modal-upload-multisource/ModalUploadMultisource.constants';
import { filesToPickedImages } from 'src/_shared/utils/filesToPickedImages';
import { useStore } from 'src/contexts/StoreContext';
import { FocusGroups } from '@photosi/rn-components/focus-provider/FocusProvider.constants';
import { brand, neutrals } from 'src/theme/colors';
import Animated, { useAnimatedStyle, useSharedValue, withSpring } from 'react-native-reanimated';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';
import { DiscountScale } from '@photosi/expo-photosi-sdk/build/Project';
import { BOTTOM_SHEET_VIEWS } from 'src/store/bottom-sheet/bottomSheet.constants';
import { PrimaryButton } from 'src/editor-v3/components/buttons/primary-button/PrimaryButton';
import { BUTTON_LAYOUTS, BUTTON_SIZES, BUTTON_TYPES } from 'src/editor-v3/components/buttons/button/Button.constants';
import { BoxStyleInfo, FormatInfo, LookupChoiceForStep } from '@photosi/expo-photosi-sdk/build/PrintUtils';
import { useNavigation } from '@react-navigation/native';
import { useTypedRoute } from 'src/editor-v3/hooks/useTypedRoute';
import { ROUTE_NAMES } from 'src/routes/_shared/routes.constants';
import { navigationResetTo } from 'src/editor-v3/helpers/navigation.helper';
import { DRAWER_POSITIONS, DRAWER_VIEWS } from 'src/store/drawer/drawer.constants';
import { useCustomScrollbar } from 'src/hooks/use-custom-visible-scrollbar/useCustomVisibleScrollbar';
import { PickedImage } from '@photosi/expo-photosi-sdk/build/ImageLibrary';
import { Icon } from '@photosi/rn-components/icon/Icon';
import { ICON_NAMES } from '@photosi/rn-components/icon/icon.helper';
import { Picker } from './pickers/Picker';
import { TestIDs } from 'src/constants/testIDs.constants';
import { PickerSourceButton } from '../../picker-source-button/PickerSourceButton';

export interface PickerStepProps {
  cards: SELECTED_MENU_ITEMS[];
  setUploadingPhoto?: (uploading: boolean) => void;
  onConfirmStep: (error?: boolean, productImages?: PickedImage[]) => void;
  discount?: DiscountScale[];
  selectedFormat?: FormatInfo | BoxStyleInfo | LookupChoiceForStep;
  isHappyCaseGroup?: boolean;
  isPhotokitGroup?: boolean;
  isOnline: boolean;
}

export const PickerStep = observer((props: PickerStepProps) => {
  const {
    project,
    i18n,
    toastStore,
    bottomSheet: { openBottomSheet, closeBottomSheet },
    drawer,
    data: { isReplacingPhoto },
  } = useStore();
  const dropZoneRef = useRef(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const { cards, setUploadingPhoto, discount, selectedFormat, isHappyCaseGroup, isPhotokitGroup } = props;
  const { isDesktop } = useResponsiveScreen();
  const [selectedMenu, setSelectedMenu] = useState<SELECTED_MENU_ITEMS>(
    isDesktop ? SELECTED_MENU_ITEMS.Computer : SELECTED_MENU_ITEMS.Gallery,
  );
  const orderedCards = useMemo(() => [...cards.filter(c => c !== selectedMenu)], [selectedMenu, cards]);
  const [isLoadingGoogle, setIsLoadingGoogle] = useState(false);
  const progressSV = useSharedValue(1);
  const [reduceMotion, setReduceMotion] = useState(false);
  const navigation = useNavigation();
  const route = useTypedRoute<ROUTE_NAMES.Upload>();
  const params = route.params ?? {};
  const { groupCodeExternal, skipConfigurationStep, skipStepValue, ...filteredParams } = params;
  const scrollViewRef = useCustomScrollbar();

  useEffect(() => {
    AccessibilityInfo.isReduceMotionEnabled().then(setReduceMotion);
    const sub = AccessibilityInfo.addEventListener('reduceMotionChanged', setReduceMotion);
    return () => sub.remove();
  }, []);

  useEffect(() => {
    if (reduceMotion) {
      progressSV.value = 1;
      return;
    }
    progressSV.value = 0;
    progressSV.value = withSpring(1, {
      damping: 50,
      stiffness: 600,
    });
  }, [selectedMenu, reduceMotion]);

  useEffect(() => {
    if (!isWeb() || selectedMenu !== SELECTED_MENU_ITEMS.Computer) return;

    const dropZone = dropZoneRef.current as any;
    if (!dropZone) return;

    const handleDragOver = (event: DragEvent) => {
      if (!isDragging) setIsDragging(true);
      event.preventDefault();
    };

    const handleDragEnter = (event: DragEvent) => {
      setIsDragging(true);
      event.preventDefault();
    };

    const handleDragLeave = (event: DragEvent) => {
      setIsDragging(false);
      event.preventDefault();
    };

    const handleDrop = async (event: DragEvent) => {
      event.preventDefault();
      setUploadingPhoto && setUploadingPhoto(true);
      setIsDragging(false);
      if (!event.dataTransfer) return;
      const droppedFiles: File[] = [...event.dataTransfer.files];
      const files = await filesToPickedImages(
        droppedFiles,
        {
          i18n,
          errorCallback: ({ areAllPhotosInvalid, errorMessage }) => {
            toastStore.showErrorToast({
              id: 'alert-uploading-photos-error-toast',
              message: errorMessage,
            });
          },
        },
        true,
      );

      files.forEach(file => (file.source = SELECTED_MENU_ITEMS.Computer));
      if (!isReplacingPhoto && files.length > 0) {
        props.onConfirmStep(false, files);
      } else {
        const image = project?.activeGroup?.userImagesWithQualityAlert.find(e => e.id === isReplacingPhoto);
        image &&
          project.replaceUserImage({
            pickedImage: files[0],
            googleAccessToken: '',
            productId: image?.productId,
            userImageId: image?.id,
          });
      }
      amplitude.trackEvent(AnalyticsEvents.dynamicPicker.sourcePhotoUploaded, {
        n_photo: files.length,
        source_type: SourcesEventType.Computer,
      });

      setUploadingPhoto && setUploadingPhoto(false);
    };

    dropZone.addEventListener('dragover', handleDragOver);
    dropZone.addEventListener('dragenter', handleDragEnter);
    dropZone.addEventListener('dragleave', handleDragLeave);
    dropZone.addEventListener('drop', handleDrop);

    return () => {
      dropZone.removeEventListener('dragover', handleDragOver);
      dropZone.removeEventListener('dragenter', handleDragEnter);
      dropZone.removeEventListener('dragleave', handleDragLeave);
      dropZone.removeEventListener('drop', handleDrop);
    };
  }, [selectedMenu, isDragging, props, setUploadingPhoto, i18n, toastStore, project]);

  const OtherChoises = useCallback(
    ({ source, index }: { source: SELECTED_MENU_ITEMS; index: number }) => {
      const source_type = SelectedMenuItem2SourcesEventTypeMap[source];

      return (
        <PickerSourceButton
          style={styles.pickerButton}
          innerStyle={
            !isDesktop && {
              flexDirection: 'row',
            }
          }
          source={source}
          isSelected={false}
          onPress={async () => {
            setSelectedMenu(source);
            if (source_type) {
              amplitude.trackEvent(AnalyticsEvents.dynamicPicker.sourceClick, { source_type });
            }
          }}
          text={i18n.t(`upload:${source}`)}
          focusId={`picker-choose-${source}`}
          focusGroup={FocusGroups.PRODUCT}
          focusOrder={index + 10}
        />
      );
    },
    [setSelectedMenu, i18n],
  );

  const animatedStyle = useAnimatedStyle(() => {
    return {
      opacity: progressSV.value,
      transform: [
        {
          translateY: (1 - progressSV.value) * 100,
        },
      ],
    };
  });

  const onOpenDiscount = async () => {
    if (selectedFormat && 'code' in selectedFormat) {
      const formatCard = selectedFormat as FormatInfo | BoxStyleInfo;
      const formattedCode = formatCard?.code?.split('.')?.[0];
      const suggestedFormatCard = {
        ...formatCard,
        title: !isHappyCaseGroup
          ? `modal-configuration-steps:format:${formattedCode}`
          : `modal-configuration-steps:box-style:${formatCard.code.toLowerCase()}`,
      };
      openBottomSheet({
        bottomSheetView: BOTTOM_SHEET_VIEWS.DiscountTable,
        bottomSheetData: discount,
        showDragIndicator: true,
        suggestedFormatCard: suggestedFormatCard,
      });
    }
  };

  const navigateToGrid = async () => {
    amplitude.trackEvent(AnalyticsEvents.dynamicPicker.continueClick, {
      n_photo: 0,
      n_photos_facebook: 0,
      n_photos_gphoto: 0,
      n_photos_local: 0,
      n_photos_smartphone: 0,
      n_photos_family_friends: 0,
    });
    project.activeProject?.deselectAllThumbnailImages();
    if (isDesktop) {
      drawer.openDrawer({
        drawerPosition: DRAWER_POSITIONS.Left,
        drawerView: DRAWER_VIEWS.PhotoDrawer,
        withBackdrop: false,
        openPoint: 'picker',
        uploadButtonCallback: async () => {
          project?.activeGroup?.checkMinMaxProductsAndNotify();
          project?.addProductsV2({
            ids: project?.activeProject?.selectedThumbnails.map(t => t.originalImageId) ?? [],
            productConfigurationId: project?.activeGroup
              ? project?.activeGroup.currectProductConfigurationId
              : project.productConfigurationId,
            groupCode: project?.activeGroup?.code || project?.groupCode,
            groupId: project?.activeGroup?.groupIdSDK,
          });
          drawer.closeDrawer();
        },
      });
    } else {
      openBottomSheet({
        bottomSheetView: BOTTOM_SHEET_VIEWS.PhotoDrawer,
        showDragIndicator: false,
        contentViewStyle: { paddingHorizontal: 0 },
        showOpacityBackground: false,
        uploadButtonCallback: async () => {
          project?.activeGroup?.checkMinMaxProductsAndNotify();
          project?.addProductsV2({
            ids: project?.activeProject?.selectedThumbnails.map(t => t.originalImageId) ?? [],
            productConfigurationId: project?.activeGroup
              ? project?.activeGroup.currectProductConfigurationId
              : project.productConfigurationId,
            groupCode: project?.activeGroup?.code || project?.groupCode,
            groupId: project?.activeGroup?.groupIdSDK,
          });
          closeBottomSheet();
        },
      });
    }
    navigationResetTo(navigation, ROUTE_NAMES.EditorNew, { ...filteredParams });
  };

  return (
    <View ref={dropZoneRef} style={{ flex: 1 }}>
      <Animated.View key={'Animated-Picker'} style={[{ flex: 1 }, animatedStyle]}>
        <ScrollView
          ref={scrollViewRef}
          style={{ flex: 1 }}
          contentContainerStyle={{
            flexGrow: 1,
            justifyContent: 'center',
            alignItems: 'center',
            gap: 16,
            paddingVertical: 24,
          }}
          showsVerticalScrollIndicator={true}
          persistentScrollbar={true}
        >
          <Picker
            focusId={`${TestIDs.PICKER_SOURCE}-${selectedMenu}`}
            focusOrder={0}
            focusGroup={FocusGroups.PRODUCT}
            source={selectedMenu}
            isDragging={isDragging}
            onLoadingChange={(isLoading: boolean) => {
              if (selectedMenu === SELECTED_MENU_ITEMS.GooglePhoto) {
                setIsLoadingGoogle(isLoading);
              }
            }}
            {...props}
          />
          {!isLoadingGoogle && (
            <>
              <TextLato style={styles.otherChoisesText} numberOfLines={1} accessibilityRole="header">
                {i18n.t('upload:upload-from-other-source')}
              </TextLato>
              {orderedCards.map((source, index) => (
                <OtherChoises source={source} index={index} key={source} />
              ))}
            </>
          )}

          {selectedMenu === SELECTED_MENU_ITEMS.FamilyFriends && (
            <View style={{ alignItems: 'center', gap: 16 }}>
              <TextLato
                style={{ fontWeight: 400, fontSize: 14, lineHeight: 21, color: neutrals.neutralGrays.graphite }}
                numberOfLines={1}
              >
                {i18n.t('modal-picker-family-friends:or-choice')}
              </TextLato>
              <PrimaryButton
                onPress={navigateToGrid}
                text={i18n.t('upload:continue')}
                accessibilityLabel={i18n.t('upload:continue')}
                focusId={'picker-choose-continue'}
                focusGroup={FocusGroups.PRODUCT}
                focusOrder={orderedCards.length + 100}
                style={{ width: 280 }}
                size={BUTTON_SIZES.REGULAR}
                type={BUTTON_TYPES.DEFAULT}
                layout={BUTTON_LAYOUTS.CENTER}
              />
            </View>
          )}
          {!isDesktop && !isPhotokitGroup && discount?.length > 1 && (
            <View style={styles.containerMobile}>
              <Icon name={ICON_NAMES.DiscountSimpleV3} color={brand.main.orange} width={20} height={20} filled />
              <TextLato style={styles.leftTextTitleMobile} numberOfLines={1}>
                {i18n.t('discounts-table:prints-title')}
              </TextLato>
              <PrimaryButton
                onPress={onOpenDiscount}
                focusId={'discounts-table-discover-discount'}
                focusOrder={0}
                focusGroup={FocusGroups.LOOKUP_CHOICE}
                text={i18n.t('discounts-table:discover-discount')}
                accessibilityLabel={i18n.t('discounts-table:discover-discount')}
                style={{ minWidth: 60, maxWidth: 60, backgroundColor: brand.main.orange }}
                layout={BUTTON_LAYOUTS.CENTER}
                size={BUTTON_SIZES.REGULAR}
              />
            </View>
          )}
        </ScrollView>
      </Animated.View>
    </View>
  );
});
